import { useEffect, useMemo, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Alert, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Chip } from "../components/Status";
import { HIST_DEFAULT, HistFilterBar, HistStats, HistTable, filterHistory, foldHistory, histTotals } from "../components/History";
import type { HistFilter } from "../components/History";
import { chatApi } from "../chat/api";
import type { Session } from "../chat/api";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { errText, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 관측 › 이력(#/history) — t-in-port-obs-history ══
   끝난 이슈마다 토큰 · 소요를 돌아본다. 세션 진행의 「이벤트 조회」(무엇이 언제 바뀌었나)와 다른 질문이라 화면을 따로 둔다.
   이슈 · 세션은 각각 한 번씩 읽고 화면이 접는다 — 세션은 서버 상한(1,000건)까지라, 꽉 차면 「오래된 이슈는 모름일 수 있음」을 말한다. */

const SESS_LIMIT = 1000;
const PAGE = 50;

export function HistoryPage() {
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistFilter>(HIST_DEFAULT);
  const [avg, setAvg] = useState(false);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    tasksApi.list().then(setIssues, (e) => setErr(errText(e)));
    chatApi.sessions(undefined, SESS_LIMIT).then(setSessions, (e) => setErr(errText(e)));
    // 프로젝트 이름은 곁가지 — 못 받으면 id 로 그린다.
    projectApi.list().then(setProjects, () => undefined);
  }, []);
  useEffect(() => setShown(PAGE), [filter]);

  const all = useMemo(() => (issues && sessions ? foldHistory(issues, sessions) : null), [issues, sessions]);
  const rows = useMemo(() => (all ? filterHistory(all, filter) : []), [all, filter]);
  const totals = useMemo(() => histTotals(rows), [rows]);
  const name = (id: string | null) => projects.find((p) => p.id === id)?.name ?? id ?? "";
  const capped = (sessions?.length ?? 0) >= SESS_LIMIT;

  return (
    <AppShell route="/history">
      <PageHead
        title={tx("nav.history")}
        lead={<p>{tx("hist.lead")}</p>}
        meta={all ? <Chip tone="muted">{tx("hist.count", { n: rows.length, m: totals.measured })}</Chip> : null}
      />
      <HistFilterBar value={filter} projects={projects} avg={avg} onChange={setFilter} onAvg={setAvg} />
      {err ? <Alert tone="error">{tx("hist.loadFailed", { err })}</Alert> : null}
      {!all && !err ? <Loading /> : null}
      {all ? (
        <>
          <HistStats t={totals} avg={avg} />
          {capped ? <p className="caption hist-note">▲ {tx("hist.capped")}</p> : null}
          <HistTable rows={rows} projectName={name} shown={shown} onMore={() => setShown((n) => n + PAGE)} />
        </>
      ) : null}
    </AppShell>
  );
}
